import type { ComponentType, LazyExoticComponent } from "react";
import React, { Suspense, lazy } from "react";
import { Outlet, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Box, CircularProgress, ScopedCssBaseline } from "@mui/material";

import { RequireAuth } from "../Pages/auth/require_auth";
import { useSyncActiveOrgFromUrl } from "../hooks/useSyncActiveOrgFromUrl";

import SwitchingOrgLoader from "./common/SwitchingOrgLoader";
import { NewLayout } from "./nav/NewLayout";

/**
 * Legacy org-scoped URLs (/organization/:organization_code/...) act as an implicit org
 * switch. Handling it here — one place every route flows through — keeps the switcher in
 * sync for all org-scoped routes without each page opting in. No-op when the URL has no
 * org code. See useSyncActiveOrgFromUrl.
 */
const OrgSyncGate = ({ children }: { children: React.ReactNode }) => {
  const isSwitchingOrg = useSyncActiveOrgFromUrl();
  return isSwitchingOrg ? <SwitchingOrgLoader /> : <>{children}</>;
};

const RouteWithTitle = ({
  title,
  component,
  requireAuth,
}: {
  title: string;
  component?: LazyExoticComponent<ComponentType<any>> | ComponentType<any> | any; // we need to use any here because of the use of old components in the codebase;
  requireAuth?: boolean;
}) => {
  const Component = component;
  document.title = title === "" ? "TheirStory" : `${title} - TheirStory`;

  const routeContent = (
    <Suspense
      fallback={
        <Box
          sx={{
            minHeight: "40vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      }
    >
      <OrgSyncGate>
        <Component />
      </OrgSyncGate>
    </Suspense>
  );

  if (requireAuth) {
    return <RequireAuth>{routeContent}</RequireAuth>;
  }

  return routeContent;
};

// Lazy-loaded components
const MagicLink = lazy(() => import("../Pages/auth/magicLink"));
const JoinProjectMagicLink = lazy(() => import("../Pages/auth/joinProjectMagicLink"));
const JoinOrganizationMagicLink = lazy(() => import("../Pages/auth/joinOrganizationMagicLink"));
const ErrorPage = lazy(() => import("../Pages/ErrorPages/ErrorPage"));
const PasswordRecovery = lazy(() => import("../Pages/auth/passwordRecovery"));
const PasswordUpdate = lazy(() => import("../Pages/auth/passwordUpdate"));
const SSOIdentityProviderDiscovery = lazy(() => import("../Pages/auth/ssoIdentityProviderDiscovery"));
const Signout = lazy(() => import("../Pages/auth/signout"));
const Ticket = lazy(() => import("../Pages/auth/ticket"));
const TicketUsed = lazy(() => import("../Pages/admin/ticketUsed"));
const Admin = lazy(() => import("../Pages/admin/admin"));
const OrganizationDetails = lazy(() => import("../Pages/admin/organizationDetails"));
const NewProjectConclude = lazy(() => import("../Pages/ConcludeV2/ConcludePage"));
const Hub = lazy(() => import("../Pages/Hub/HubPage"));
const LinkExpired = lazy(() => import("./link_expired"));
const PrepareCampaign = lazy(() => import("../Pages/PrepareCampaign/PrepareCampaignPage"));
const PlanCall = lazy(() => import("../Pages/PlanCall/PlanCallPage"));
const StoryAuthoring = lazy(() => import("../Pages/story_authoring"));
const ThankYou = lazy(() => import("../Pages/thankYou/thank_you"));
const UploadFiles = lazy(() => import("../Pages/UploadFiles/UploadFiles"));
const SelectCloudFiles = lazy(() => import("../Pages/UploadFiles/SelectCloudFiles"));
const LocalRecordingStatus = lazy(() => import("./utils/status/all_local_recording_uploads"));
const Home = lazy(() => import("../Pages/home/home"));
const MyInterviews = lazy(() => import("../Pages/MyInterviews/MyInterviews"));
const Clips = lazy(() => import("../Pages/Clips/ClipsPage"));
const ThemesPage = lazy(() => import("../Pages/Themes/ThemesPage"));
const RemixerPage = lazy(() => import("../Pages/Remixes/RemixerPage/RemixerPage"));
const RemixesPage = lazy(() => import("../Pages/Remixes/RemixesPage/RemixesPage"));
const Login = lazy(() => import("../Pages/auth/login"));
const OrganizationsPage = lazy(() => import("../Pages/Organization/OrganizationsPage"));
const ProjectPage = lazy(() => import("../Pages/Project/ProjectPage"));
const ImportMetadataPage = lazy(() => import("../Pages/Project/ImportMetadata/ImportMetadataPage"));
const DiscoverPage = lazy(() => import("../Pages/Project/Discover/DiscoverPage"));
const FolderPage = lazy(() => import("../Pages/Folder/FolderPage"));
const ProjectsPage = lazy(() => import("../Pages/Projects/ProjectsPage"));
const GalleriesPage = lazy(() => import("../Pages/Galleries/GalleriesPage/GalleriesPage"));
const GalleryPage = lazy(() => import("../Pages/Galleries/GalleryPage/GalleryPage"));
const RefineGallery = lazy(() => import("../Pages/Galleries/RefineGallery/RefineGallery"));
const PortalsPage = lazy(() => import("../Pages/Portals/PortalsPage/PortalsPage"));
const PortalPage = lazy(() => import("../Pages/Portals/PortalPage/PortalPage"));
const EditMetadataFiles = lazy(() => import("../Pages/UploadFiles/editMetadataFiles"));
const DailyRoomCreation = lazy(() => import("./InterviewDaily/index"));
const RoomDaily = lazy(() => import("./InterviewDaily/RoomDaily"));
const PrepSessionRedirection = lazy(() => import("../Pages/prepSessionRedirection/prepRedirection"));
const Metadata = lazy(() => import("../Pages/story_authoring/Metadata/Metadata"));
const Notes = lazy(() => import("../Pages/story_authoring/Index/Notes"));
const RedirectMarketingSite = lazy(() => import("../Pages/RedirectMarketingSite/RedirectMarketingSite"));
const TestimonialsControls = lazy(() => import("../Pages/admin/testimonialsControls"));
const EntitiesPage = lazy(() => import("../Pages/Entities/EntitiesPage"));
const Playground = lazy(() => import("../Pages/Playground/Playground"));
const CallAccessBridge = lazy(() =>
  import("./CallAccessBridge").then(({ CallAccessBridge: Component }) => ({ default: Component }))
);

const routes = [
  // Home
  { path: "/", component: Home, title: "Home", requireAuth: true },

  // Redirects to marketing site
  { path: "/welcome", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  {
    path: "/terms-and-conditions",
    component: () => <RedirectMarketingSite sectionUrl="/terms-and-conditions" />,
    title: "Redirect to Marketing Site",
    requireAuth: false,
  },
  {
    path: "/privacy-policy",
    component: () => <RedirectMarketingSite sectionUrl="/privacy-policy" />,
    title: "Redirect to Marketing Site",
    requireAuth: false,
  },
  {
    path: "/about-us",
    component: () => <RedirectMarketingSite sectionUrl="/who-we-are" />,
    title: "Redirect to Marketing Site",
    requireAuth: false,
  },
  { path: "/getting-started", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/FAQ", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/individuals", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/communities", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/features", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/case-study/1", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/case-study/2", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },
  { path: "/case-study/3", component: RedirectMarketingSite, title: "Marketing Site", requireAuth: false },

  // Authentication and User Management
  { path: "/login", component: Login, title: "Login", requireAuth: false },
  {
    path: "/signup",
    component: RedirectMarketingSite,
    title: "Redirect to Home",
    requireAuth: false,
  },
  { path: "/sso", component: SSOIdentityProviderDiscovery, title: "Single Sign-On", requireAuth: false },
  { path: "/password_recovery", component: PasswordRecovery, title: "Password Recovery", requireAuth: false },
  { path: "/password/new/:token", component: PasswordUpdate, title: "Password Update", requireAuth: false },
  { path: "/signout", component: Signout, title: "Signout", requireAuth: false },

  // Organizations
  { path: "/organizations", component: OrganizationsPage, title: "Organizations", requireAuth: true },
  { path: "/admin", component: Admin, title: "Admin Dashboard", requireAuth: true },
  {
    path: "/admin/organization/:organization_code",
    component: OrganizationDetails,
    title: "Organization Settings",
    requireAuth: true,
  },
  {
    path: "/admin/testimonials-controls",
    component: TestimonialsControls,
    title: "Testimonials Controls",
    requireAuth: true,
  },
  {
    path: "/admin/playground",
    component: Playground,
    title: "Semantic Search Playground",
    requireAuth: true,
  },

  // Projects
  { path: "/projects", component: ProjectsPage, title: "Projects", requireAuth: true },
  { path: "/projects/:project_id", component: ProjectPage, title: "Project", requireAuth: true },
  { path: "/projects/:project_id/discover", component: DiscoverPage, title: "Discover", requireAuth: true },
  {
    path: "/projects/:project_id/import-metadata",
    component: ImportMetadataPage,
    title: "Import Metadata",
    requireAuth: true,
  },
  {
    path: "/organization/:organization_code/org-settings",
    component: ProjectsPage,
    title: "Organization Settings",
    requireAuth: true,
  },
  {
    path: "/organization/:organization_code/projects",
    component: ProjectsPage,
    title: "Organization Projects",
    requireAuth: true,
  },
  {
    path: "/organization/:organization_code/projects/:project_id",
    component: ProjectPage,
    title: "Organization Project",
    requireAuth: true,
  },
  {
    path: "/organization/:organization_code/projects/:project_id/discover",
    component: DiscoverPage,
    title: "Discover",
    requireAuth: true,
  },
  {
    path: "/projects/:project_id/folders/:folder_id/uploadingMetadata",
    component: EditMetadataFiles,
    title: "Uploading Metadata",
    requireAuth: true,
  },
  {
    path: "/projects/:project_id/uploadingMetadata",
    component: EditMetadataFiles,
    title: "Uploading Metadata",
    requireAuth: true,
  },

  // Folders
  {
    path: "/organization/:organization_code/projects/:project_id/folders/:folder_id",
    component: FolderPage,
    title: "Folder",
    requireAuth: true,
  },
  {
    path: "/projects/:project_id/folders/:folder_id",
    component: FolderPage,
    title: "Project Folder",
    requireAuth: true,
  },

  // Stories
  {
    path: "organization/:organization_id/projects/:project_id/folders/:folder_id/stories/:story_id/hub",
    component: Hub,
    title: "Story Hub",
    requireAuth: true,
  },
  {
    path: "organization/:organization_id/projects/:project_id/stories/:story_id/hub",
    component: Hub,
    title: "Story Hub",
    requireAuth: true,
  },
  {
    path: "projects/:project_id/folders/:folder_id/stories/:story_id/hub",
    component: Hub,
    title: "Story Hub",
    requireAuth: true,
  },
  { path: "projects/:project_id/stories/:story_id/hub", component: Hub, title: "Story Hub", requireAuth: true },
  { path: "myInterviews/stories/:story_id/hub", component: Hub, title: "Story Hub", requireAuth: true },
  { path: "/stories/:story_id/conclude", component: NewProjectConclude, title: "Conclude Story", requireAuth: false },
  {
    path: "/projects/:project_id/stories/:story_id/conclude",
    component: NewProjectConclude,
    title: "Conclude Story",
    requireAuth: true,
  },
  {
    path: "/projects/:project_id/folders/:folder_id/stories/:story_id/conclude",
    component: NewProjectConclude,
    title: "Conclude Story",
    requireAuth: true,
  },
  { path: "/stories/:story_id/thank-you", component: ThankYou, title: "Thank You", requireAuth: false },
  { path: "/stories/:story_id/link-expired", component: LinkExpired, title: "Link Expired", requireAuth: false },
  {
    path: "/stories/:story_id/author",
    component: StoryAuthoring,
    title: "Author Story",
    requireAuth: false,
    children: [
      { index: true, component: StoryAuthoring, title: "Story Authoring", requireAuth: false },
      { path: ":index_id/metadata", component: Metadata, title: "Story Authoring Index", requireAuth: false },
      { path: ":index_id/notes", component: Notes, title: "Story Authoring Index: Notes Mode", requireAuth: false },
    ],
  },
  {
    path: "projects/:project_id/stories/:story_id/edit",
    component: NewProjectConclude,
    title: "Edit Story",
    requireAuth: true,
  },
  {
    path: "projects/:project_id/folders/:folder_id/stories/:story_id/edit",
    component: NewProjectConclude,
    title: "Edit Story",
    requireAuth: true,
  },

  // Interviews
  { path: "/myInterviews", component: MyInterviews, title: "My Interviews", requireAuth: true },

  // Daily Rooms
  { path: "/call-access", component: CallAccessBridge, title: "", requireAuth: false },
  {
    path: "/projects/:project_id/room/start",
    component: DailyRoomCreation,
    title: "Daily Room Creation",
    requireAuth: false,
  },
  {
    path: "/projects/:project_id/folders/:folder_id/room/start",
    component: DailyRoomCreation,
    title: "Daily Room Creation",
    requireAuth: false,
  },
  { path: "/room/stories/:story_id", component: RoomDaily, title: "Story Room", requireAuth: false },
  { path: "room/session/:interviewId", component: RoomDaily, title: "Room Session", requireAuth: false },

  // Campaigns
  {
    path: "/plan_campaign/projects/:project_id",
    component: PrepareCampaign,
    title: "Plan Campaign",
    requireAuth: true,
  },
  {
    path: "/plan_campaign/organizations/:organization_code/projects/:project_id",
    component: PrepareCampaign,
    title: "Plan Campaign",
    requireAuth: true,
  },
  {
    path: "/plan_campaign/projects/:project_id/prepsession/:prepsession_id",
    component: PrepareCampaign,
    title: "Campaign Preparation",
    requireAuth: true,
  },
  { path: "/plan_call/projects/:project_id", component: PlanCall, title: "Plan Call", requireAuth: true },
  {
    path: "/plan_call/organizations/:organization_code/projects/:project_id",
    component: PlanCall,
    title: "Plan Call",
    requireAuth: true,
  },
  {
    path: "/plan_call/projects/:project_id/prepsession/:prepsession_id",
    component: PlanCall,
    title: "Call Preparation",
    requireAuth: true,
  },

  // Files
  { path: "/projects/:project_id/upload_files", component: UploadFiles, title: "Upload Files", requireAuth: true },
  {
    path: "/projects/:project_id/folders/:folder_id/upload_files",
    component: UploadFiles,
    title: "Folder Upload Files",
    requireAuth: true,
  },
  { path: "/projects/select_cloud_files", component: SelectCloudFiles, title: "Select Cloud Files", requireAuth: true },

  // Galleries
  { path: "/galleries", component: GalleriesPage, title: "Galleries", requireAuth: true },
  { path: "/galleries/:gallery_id", component: GalleryPage, title: "Gallery", requireAuth: true },
  { path: "/galleries/newGallery", component: GalleryPage, title: "New Gallery", requireAuth: true },
  { path: "/galleries/edit_gallery/:gallery_id", component: GalleryPage, title: "Edit Gallery", requireAuth: true },
  { path: "/galleries/refine_gallery/:gallery_id", component: RefineGallery, title: "Edit Gallery", requireAuth: true },

  // Portals
  { path: "/portals", component: PortalsPage, title: "Portals", requireAuth: true },
  { path: "/portals/:portal_id", component: PortalPage, title: "Portal", requireAuth: true },

  // Clips
  { path: "/clips", component: Clips, title: "Clips", requireAuth: true },

  // Themes (org-level; flag-gated in the page + nav)
  { path: "/themes", component: ThemesPage, title: "Themes", requireAuth: true },

  // Remixer
  { path: "/remixes", component: RemixesPage, title: "Remixes", requireAuth: true },
  { path: "/remixes/:remixer_id", component: RemixerPage, title: "Remixer", requireAuth: true },

  // Entities
  { path: "/entities", component: RemixesPage, title: "Entities", requireAuth: true },
  { path: "/entities/:story_id", component: EntitiesPage, title: "Entities", requireAuth: true },

  // Miscellaneous
  {
    // Redirect for prepared sessions
    path: "/:group_name/:custom_url_title/s/:key/:prepSession_type",
    component: PrepSessionRedirection,
    title: "Session Redirect",
    requireAuth: false,
  },
  { path: "/magic-link", component: MagicLink, title: "Magic Link", requireAuth: false },
  { path: "/project/:code", component: JoinProjectMagicLink, title: "Join Project", requireAuth: false },
  {
    path: "/organization/:code/accept",
    component: JoinOrganizationMagicLink,
    title: "Join Organization",
    requireAuth: false,
  },
  { path: "/ticket/:ticket", component: Ticket, title: "Ticket", requireAuth: true },
  { path: "/share/:ticket", component: Ticket, title: "Share Ticket", requireAuth: false },
  { path: "/ticketUsed", component: TicketUsed, title: "Ticket Used", requireAuth: true },
  {
    path: "/status/local-recording",
    component: LocalRecordingStatus,
    title: "Local Recording Status",
    requireAuth: true,
  },
];

const SentryRoutes = Sentry.withSentryRouting(Routes);

function AppRoutes() {
  return (
    <SentryRoutes>
      <Route
        element={
          <ScopedCssBaseline style={{ height: "100%" }}>
            <NewLayout>
              <Outlet />
            </NewLayout>
          </ScopedCssBaseline>
        }
      >
        {routes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={<RouteWithTitle title={route.title} component={route.component} requireAuth={route.requireAuth} />}
          >
            {route.children &&
              route.children.map((child, childIndex) => (
                <Route
                  key={childIndex}
                  path={child.path}
                  index={child.index}
                  element={
                    <RouteWithTitle title={child.title} component={child.component} requireAuth={child.requireAuth} />
                  }
                />
              ))}
          </Route>
        ))}
      </Route>
      <Route path="*" element={<ErrorPage />} />
    </SentryRoutes>
  );
}

export default Sentry.withProfiler(AppRoutes);
